import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import './TermsConditionsPopup.css';

const TermsConditionsPopup = ({ isOpen, onClose, contentType, onTermsAccepted }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Content configurations
  const contentConfig = {
    'terms-and-conditions': {
      title: 'Terms & Conditions',
      slug: 'terms-and-conditions'
    },
    'refund-cancellation-policy': {
      title: 'Refund & Cancellation Policy',
      slug: 'refund-cancellation-policy'
    },
    'privacy-policy': {
      title: 'Privacy Policy',
      slug: 'privacy-policy'
    }
  };

  const currentConfig = contentConfig[contentType] || contentConfig['terms-and-conditions'];

  // Fetch content when popup opens
  useEffect(() => {
    if (!isOpen || !contentType) return;

    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const pageData = await api.getCmsPage(currentConfig.slug);
        if (pageData && pageData.content) {
          setContent(pageData.content);
        } else {
          // Fallback content if API fails
          setContent(getFallbackContent(contentType));
        }
      } catch (error) {
        console.error('Error fetching terms content:', error);
        // Use fallback content on error
        setContent(getFallbackContent(contentType));
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [isOpen, contentType, currentConfig.slug]);

  const getFallbackContent = (type) => {
    const fallbacks = {
      'terms-and-conditions': `
        <h2>Terms & Conditions</h2>
        <p><strong>Last Updated: January 2025</strong></p>
        
        <h3>1. Booking Terms</h3>
        <p>By booking tickets through MY Cinemas, you agree to these terms and conditions.</p>
        <ul>
          <li>All bookings are subject to availability</li>
          <li>Tickets are non-refundable except as specified in our cancellation policy</li>
          <li>Prices are subject to change without prior notice</li>
          <li>Online booking fees may apply</li>
        </ul>

        <h3>2. Cinema Rules</h3>
        <p>Patrons must adhere to cinema rules and regulations:</p>
        <ul>
          <li>No outside food or beverages allowed</li>
          <li>Mobile phones must be on silent mode during screenings</li>
          <li>Recording or photography is strictly prohibited</li>
          <li>Age restrictions will be strictly enforced</li>
        </ul>

        <h3>3. Payment Terms</h3>
        <p>Payment must be completed at the time of booking:</p>
        <ul>
          <li>We accept credit/debit cards, net banking, and digital wallets</li>
          <li>All transactions are secured with industry-standard encryption</li>
          <li>Additional payment gateway fees may apply</li>
        </ul>

        <h3>4. Cancellation & Refunds</h3>
        <p>Refunds are processed according to our cancellation policy:</p>
        <ul>
          <li>Cancellations must be made at least 2 hours before showtime</li>
          <li>Cancellation fees may apply</li>
          <li>Refunds will be processed to the original payment method</li>
        </ul>

        <h3>5. Privacy & Data</h3>
        <p>We respect your privacy and handle your data in accordance with applicable laws.</p>
        
        <h3>6. Contact Information</h3>
        <p>For questions about these terms, please contact our customer service.</p>
      `,
      
      'refund-cancellation-policy': `
        <h2>Refund & Cancellation Policy</h2>
        <p><strong>Last Updated: January 2025</strong></p>
        
        <h3>1. Cancellation Policy</h3>
        <p>Customers can cancel their bookings under the following conditions:</p>
        <ul>
          <li>Cancellations allowed up to 2 hours before showtime</li>
          <li>50% refund for cancellations made 2-4 hours before showtime</li>
          <li>No refund for cancellations made less than 2 hours before showtime</li>
          <li>Full refund for cancellations due to technical issues</li>
        </ul>

        <h3>2. Refund Process</h3>
        <p>Refunds are processed as follows:</p>
        <ul>
          <li>Refunds initiated within 5-7 working days</li>
          <li>Amount credited to original payment method</li>
          <li>Processing time depends on your bank/payment provider</li>
          <li>You will receive email confirmation of refund initiation</li>
        </ul>

        <h3>3. Show Cancellations</h3>
        <p>In case of show cancellations by the cinema:</p>
        <ul>
          <li>100% refund automatically processed</li>
          <li>Alternative show options may be offered</li>
          <li>Notification sent via email and SMS</li>
        </ul>

        <h3>4. Special Circumstances</h3>
        <p>Refunds may be considered for:</p>
        <ul>
          <li>Medical emergencies (with documentation)</li>
          <li>Technical issues during screening</li>
          <li>Force majeure events</li>
        </ul>

        <h3>5. How to Cancel</h3>
        <p>To cancel your booking:</p>
        <ul>
          <li>Log into your MY Cinemas account</li>
          <li>Go to "My Bookings" section</li>
          <li>Select the booking you wish to cancel</li>
          <li>Click on "Cancel Booking" and confirm</li>
        </ul>
      `,
      
      'privacy-policy': `
        <h2>Privacy Policy</h2>
        <p><strong>Last Updated: January 2025</strong></p>
        
        <h3>1. Information We Collect</h3>
        <p>We collect the following types of information:</p>
        <ul>
          <li>Personal information (name, email, phone number)</li>
          <li>Payment information (processed securely by payment gateways)</li>
          <li>Booking history and preferences</li>
          <li>Technical information (IP address, browser type)</li>
        </ul>

        <h3>2. How We Use Your Information</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Process your bookings and payments</li>
          <li>Send booking confirmations and updates</li>
          <li>Improve our services and user experience</li>
          <li>Send promotional offers (with your consent)</li>
          <li>Comply with legal requirements</li>
        </ul>

        <h3>3. Data Security</h3>
        <p>We protect your data by:</p>
        <ul>
          <li>Using industry-standard encryption</li>
          <li>Limiting access to authorized personnel</li>
          <li>Regular security audits and updates</li>
          <li>Secure payment processing partners</li>
        </ul>

        <h3>4. Sharing Your Information</h3>
        <p>We may share your information with:</p>
        <ul>
          <li>Cinema partners for booking fulfillment</li>
          <li>Payment processors for transaction processing</li>
          <li>Legal authorities when required by law</li>
          <li>We never sell your personal information to third parties</li>
        </ul>

        <h3>5. Your Rights</h3>
        <p>You have the right to:</p>
        <ul>
          <li>Access your personal information</li>
          <li>Correct inaccurate information</li>
          <li>Request deletion of your data</li>
          <li>Opt-out of marketing communications</li>
        </ul>

        <h3>6. Cookies</h3>
        <p>We use cookies to:</p>
        <ul>
          <li>Remember your preferences</li>
          <li>Analyze website traffic</li>
          <li>Improve user experience</li>
          <li>You can control cookies through your browser settings</li>
        </ul>

        <h3>7. Contact Us</h3>
        <p>For privacy-related questions, contact our Data Protection Officer.</p>
      `
    };
    
    return fallbacks[type] || fallbacks['terms-and-conditions'];
  };

  const handleAcceptTerms = () => {
    setTermsAccepted(true);
    if (onTermsAccepted) {
      onTermsAccepted(contentType);
    }
    onClose();
  };

  const handleClose = () => {
    setTermsAccepted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="terms-conditions-overlay">
      <div className="terms-conditions-popup">
        {/* Header */}
        <div className="terms-header">
          <h3>{currentConfig.title}</h3>
          <button className="terms-close-btn" onClick={handleClose}>
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="terms-content">
          {loading ? (
            <div className="terms-loading">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p>Loading terms and conditions...</p>
            </div>
          ) : error ? (
            <div className="terms-error">
              <p>Unable to load content. Please try again later.</p>
              <button onClick={handleClose} className="btn btn-secondary">
                Close
              </button>
            </div>
          ) : (
            <div 
              className="terms-text"
              dangerouslySetInnerHTML={{ __html: content }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="terms-footer">
          {onTermsAccepted && (
            <div className="terms-accept-section">
              <label className="terms-accept-label">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="terms-accept-checkbox"
                />
                <span>I have read and accept the {currentConfig.title}</span>
              </label>
            </div>
          )}
          
          <div className="terms-actions">
            {onTermsAccepted && (
              <button 
                onClick={handleAcceptTerms}
                disabled={!termsAccepted}
                className="btn btn-primary terms-accept-btn"
              >
                Accept & Continue
              </button>
            )}
            <button 
              onClick={handleClose}
              className="btn btn-secondary terms-close-btn-footer"
            >
              {onTermsAccepted ? 'Cancel' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPopup;
